/* Roofing department - global storefront enhancements.
   Loaded from layout/theme.liquid only when Theme settings > Roofing
   department > Enable Roofing department is on.

   Moved unchanged from theme-global.js (FRK PDP header refinement):
   - refines visible roofing-kit title copy (duty labels, m2 -> m²) wherever
     product titles appear (cards, search, cart drawer, quick view, sticky ATC)
   - enhances the roofing PDP price/colour summary (.frk-pdp-summary) */
(() => {
  const PRODUCT_TITLE_SELECTORS = [
    '.m-product-title',
    '.frk-kit-card-title-main',
    '.m-product-card__name',
    '.frk-predictive-product__title',
    '.m-search-item--title',
    '.m-cart-drawer-item__title',
    '.m-cascading-product-card__title h4',
    '.m-lookbook-product__title',
    '.m-stl-product__title',
    '.m-sticky-addtocart--title h3',
    '.m-testimonial__product-title',
    '.m-image-comparison__product-title',
    '.product-bundles__item-title'
  ].join(',');

  /* Only roofing titles get roofing refinement, so ordinary GRP product
     titles are never touched. A title is roofing when it names a roof product
     (every roofing kit contains "Roofing Kit"/"Roof Kit", roofing tool kits
     contain "Roof") or carries the kit duty wording in brackets, which the
     shortened kit-card and cart-drawer titles keep after "Roofing Kit" is
     stripped from them. */
  const ROOFING_TITLE_PATTERN = /\broof|\(\s*(?:light|standard|heavy)\s+duty\s*\)|\(\s*balcony\s*\)/i;
  // Kit-branch card title only (snippets/product-card-1.liquid); every other
  // card title also uses .frk-kit-card-title-main, so the class alone isn't enough.
  const ROOFING_TITLE_ELEMENT = '[data-roofing-kit-title]';

  function isRoofingTitle(element, text) {
    return element.matches(ROOFING_TITLE_ELEMENT) || ROOFING_TITLE_PATTERN.test(text);
  }

  function refineRoofingKitTitleText(value, is450g = false) {
    let text = String(value || '').replace(/\s+/g, ' ').trim();
    const contains450g = is450g || /\b450\s*g\b/i.test(text);

    text = text.replace(/(\d+(?:\.\d+)?)\s*m2\b/gi, '$1m²');

    // The Shopify/BC title remains unchanged. This only refines visible storefront copy.
    if (contains450g) {
      text = text.replace(/\s*\(\s*light\s+duty\s*\)\s*/gi, ' – Standard Duty ');
      text = text.replace(/\s+light\s+duty\b/gi, ' – Standard Duty');
    }

    // Retain the existing cleaner presentation for duty labels already held in brackets.
    text = text.replace(/\s*\(\s*standard\s+duty\s*\)\s*/gi, ' – Standard Duty ');
    text = text.replace(/\s*\(\s*heavy\s+duty\s*\)\s*/gi, ' – Heavy Duty ');
    text = text.replace(/\s*\(\s*balcony\s*\)\s*/gi, ' – Balcony ');

    return text.replace(/\s+/g, ' ').trim();
  }

  function elementRepresents450gProduct(element) {
    if (/\b450\s*g\b/i.test(element.textContent || '')) return true;

    const card = element.closest('.m-product-card, .frk-predictive-product, .m-search-item, .m-cart-drawer-item');
    if (!card) return false;

    const labelled = card.querySelector('[aria-label*="450g" i], [title*="450g" i]');
    return Boolean(labelled || /\b450\s*g\b/i.test(card.textContent || ''));
  }

  function refineVisibleRoofingKitTitles(scope = document) {
    scope.querySelectorAll('.grp-free-delivery-product').forEach((badge) => {
      badge.textContent = 'Free UK delivery';
    });

    scope.querySelectorAll(PRODUCT_TITLE_SELECTORS).forEach((title) => {
      // Roofing-kit cards use nested colour/RAL markup. Refine only their title line.
      if (title.matches('.m-product-card__name') && title.querySelector('.frk-kit-card-title-main')) return;

      const before = title.textContent || '';
      if (!isRoofingTitle(title, before)) return;

      const after = refineRoofingKitTitleText(before, elementRepresents450gProduct(title));
      if (after && after !== before.replace(/\s+/g, ' ').trim()) {
        title.textContent = after;
      }
    });

    if (!ROOFING_TITLE_PATTERN.test(document.title)) return;

    const refinedDocumentTitle = refineRoofingKitTitleText(document.title, /\b450\s*g\b/i.test(document.title));
    if (refinedDocumentTitle && refinedDocumentTitle !== document.title) {
      document.title = refinedDocumentTitle;
    }
  }

  function item(className) {
    const el = document.createElement('span');
    el.className = `frk-pdp-summary__item ${className}`;
    return el;
  }

  function enhanceSummaryRows(scope = document) {
    scope.querySelectorAll('.frk-pdp-summary').forEach((summary) => {
      if (summary.dataset.frkEnhanced === 'true') return;

      const line1 = summary.querySelector('.frk-pdp-summary__line1');
      const line2 = summary.querySelector('.frk-pdp-summary__line2');
      if (!line1 && !line2) return;

      const items = document.createElement('div');
      items.className = 'frk-pdp-summary__items';

      if (line2) {
        const swatch = line2.querySelector('.frk-pdp-swatch');
        const colour = line2.querySelector('.frk-pdp-colour');
        const ral = line2.querySelector('.frk-pdp-ral');

        if (swatch || colour) {
          const colourItem = item('frk-pdp-summary__item--colour');
          if (swatch) colourItem.append(swatch);
          if (colour) colourItem.append(colour);
          items.append(colourItem);
        }

        if (ral) {
          const ralItem = item('frk-pdp-summary__item--ral');
          ralItem.append(ral);
          items.append(ralItem);
        }
      }

      if (line1) {
        const priceLink = line1.querySelector('.frk-card-price-link--m2');
        const fullText = line1.textContent.replace(/\s+/g, ' ').trim();
        const sizeMatch = fullText.match(/([0-9]+(?:\.[0-9]+)?)\s*m²\s*kit/i);
        const dutyMatch = fullText.match(/\b(Standard Duty|Heavy Duty|Balcony)\b/i);

        if (priceLink) {
          line1.className = 'frk-pdp-summary__item frk-pdp-summary__item--metric frk-pdp-summary__item--price';
          line1.replaceChildren(priceLink);
          items.append(line1);
        }

        // Roof size and system strength are already clear in the product title
        // and configurator, so they are deliberately not repeated in this summary row.
      }

      if (!items.children.length) return;
      summary.replaceChildren(items);
      summary.classList.add('frk-pdp-summary--premium');
      summary.dataset.frkEnhanced = 'true';
    });
  }

  function enhance(scope = document) {
    refineVisibleRoofingKitTitles(scope);
    enhanceSummaryRows(scope);
  }

  function run() {
    enhance(document);
    window.setTimeout(() => enhance(document), 80);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', (event) => enhance(event.target || document));

  // Cover AJAX filtering, predictive search, cart-drawer refreshes and quick-view content.
  //
  // This observes the whole document subtree (deliberately broad - title text
  // this needs to catch can appear almost anywhere: header search results,
  // cart drawer, quick-view, AJAX-filtered collection grids). Third-party
  // scripts (analytics/pixel/review/marketing widgets) mutate the DOM
  // frequently and repeatedly while they initialise, and each of those
  // mutations used to independently queue a full-document
  // querySelectorAll(13-selector list) scan via requestAnimationFrame - one
  // scan per animation frame for as long as the mutation burst lasted.
  // Profiled: querySelector/querySelectorAll cost ~4.6s of native self time
  // on this PDP alone (4x-throttled mobile profile). This doesn't change
  // *what* runs, only batches it: real bursts of third-party-driven mutation
  // now produce one scan shortly after they quiet down, instead of one scan
  // per frame throughout. A maxWait guarantees the refinement still runs
  // periodically even if mutations never fully stop.
  let titleRefreshTimer = null;
  let titleRefreshFirstQueuedAt = 0;
  const TITLE_REFRESH_QUIET_MS = 200;
  const TITLE_REFRESH_MAX_WAIT_MS = 1000;

  const runTitleRefresh = () => {
    window.clearTimeout(titleRefreshTimer);
    titleRefreshTimer = null;
    titleRefreshFirstQueuedAt = 0;
    refineVisibleRoofingKitTitles(document);
  };

  // Ignore DOM changes that cannot contain a roofing product title, so
  // ordinary pages don't queue roofing title scans for every third-party
  // widget mutation.
  const mutationsMayContainRoofingTitle = (mutations) => mutations.some((mutation) => {
    if (mutation.type === 'characterData') return ROOFING_TITLE_PATTERN.test(mutation.target.data || '');
    for (const node of mutation.addedNodes) {
      if (node.nodeType === Node.ELEMENT_NODE && /^(SCRIPT|STYLE|LINK|META|NOSCRIPT)$/.test(node.nodeName)) continue;
      if (node.nodeType === Node.ELEMENT_NODE && (node.matches(ROOFING_TITLE_ELEMENT) || node.querySelector(ROOFING_TITLE_ELEMENT))) return true;
      if (ROOFING_TITLE_PATTERN.test(node.textContent || '')) return true;
    }
    return false;
  });

  const titleObserver = new MutationObserver((mutations) => {
    if (!mutationsMayContainRoofingTitle(mutations)) return;

    const now = Date.now();
    if (!titleRefreshFirstQueuedAt) titleRefreshFirstQueuedAt = now;

    if (now - titleRefreshFirstQueuedAt >= TITLE_REFRESH_MAX_WAIT_MS) {
      runTitleRefresh();
      return;
    }

    window.clearTimeout(titleRefreshTimer);
    titleRefreshTimer = window.setTimeout(runTitleRefresh, TITLE_REFRESH_QUIET_MS);
  });

  titleObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true
  });
})();
